import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "bc-chat-history";
const SUGGESTIONS = [
    "Quel est le prix de l'entrecôte de bœuf ?",
    "Quels sont vos horaires ?",
    "Idée recette avec le rumpsteak ?",
    "Faites-vous du traiteur ?",
];

const WELCOME = {
    role: "assistant",
    content:
        "Bonjour ! Je suis **Costo Pro**, l'assistant IA de la Boucherie Costa. Je peux vous renseigner sur nos tarifs pros, nos horaires, nos services (traiteur, prêt de matériel), et vous inspirer avec des idées de recettes. Comment puis-je vous aider aujourd'hui ?",
};

export default function ChatWidget() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState(() => {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            return parsed && parsed.length ? parsed : [WELCOME];
        } catch {
            return [WELCOME];
        }
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    // Only render for approved pros or admin
    const canUse = user && (user.role === "admin" || (user.role === "pro" && user.status === "approved"));

    useEffect(() => {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
        } catch {
            /* ignore quota */
        }
        // Scroll to bottom on new message
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        if (open && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 150);
        }
    }, [open]);

    const send = async (textOverride) => {
        const text = (textOverride ?? input).trim();
        if (!text || loading) return;
        setError(null);
        const newUser = { role: "user", content: text };
        const nextMessages = [...messages, newUser];
        setMessages(nextMessages);
        setInput("");
        setLoading(true);

        // Add an empty assistant bubble that will fill in via streaming
        setMessages((m) => [...m, { role: "assistant", content: "" }]);
        const assistantIndex = nextMessages.length; // index of the assistant bubble we just added

        try {
            const history = nextMessages.slice(0, -1).slice(-20);
            const token = localStorage.getItem("bc_access_token");
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            const response = await fetch(`${BACKEND_URL}/api/chat/stream`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ message: text, history }),
            });

            if (!response.ok) {
                if (response.status === 429) {
                    setError("Limite atteinte : réessayez dans quelques minutes.");
                } else if (response.status === 401 || response.status === 403) {
                    setError("Session expirée. Reconnectez-vous.");
                } else {
                    setError("Le service IA est momentanément indisponible.");
                }
                // Remove the empty assistant bubble
                setMessages((m) => m.filter((_, i) => i !== assistantIndex));
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let full = "";

            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                // Parse SSE events (double newline delimited)
                const parts = buffer.split("\n\n");
                buffer = parts.pop(); // keep incomplete tail
                for (const part of parts) {
                    const line = part.split("\n").find((l) => l.startsWith("data: "));
                    if (!line) continue;
                    const raw = line.slice(6).trim();
                    if (raw === "[DONE]") continue;
                    try {
                        const obj = JSON.parse(raw);
                        if (obj.delta) {
                            full += obj.delta;
                            const snapshot = full;
                            setMessages((m) => {
                                const copy = m.slice();
                                if (copy[assistantIndex]) {
                                    copy[assistantIndex] = { role: "assistant", content: snapshot };
                                }
                                return copy;
                            });
                        } else if (obj.error) {
                            setError(obj.error);
                        }
                    } catch {
                        /* ignore malformed SSE */
                    }
                }
            }
        } catch (e) {
            setError("Le service IA est momentanément indisponible.");
            setMessages((m) => m.filter((_, i) => i !== assistantIndex));
        } finally {
            setLoading(false);
        }
    };

    const resetChat = () => {
        setMessages([WELCOME]);
        setError(null);
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch {
            /* ignore */
        }
    };

    if (!canUse) return null;

    return (
        <>
            {/* Floating trigger */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="bc-chat-fab"
                aria-label={open ? "Fermer le chat" : "Ouvrir le chat"}
                data-testid="chat-fab"
            >
                {open ? <X size={22} /> : <MessageCircle size={22} />}
            </button>

            {/* Panel */}
            {open && (
                <div className="bc-chat-panel" data-testid="chat-panel" role="dialog" aria-label="Chat IA Boucherie Costa">
                    <div className="bc-chat-header">
                        <div>
                            <p
                                className="text-[10px] tracking-[0.22em] uppercase"
                                style={{ color: "#A0A0A0", fontFamily: "Montserrat, sans-serif" }}
                            >
                                Assistant IA
                            </p>
                            <h3
                                className="font-serif text-lg leading-tight"
                                style={{ color: "#D4BC7C" }}
                            >
                                Costo Pro
                            </h3>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={resetChat}
                                className="text-[10px] uppercase tracking-widest px-2 py-1 border"
                                style={{ borderColor: "rgba(212,188,124,0.4)", color: "#D4BC7C", background: "transparent" }}
                                data-testid="chat-reset"
                            >
                                Nouvelle
                            </button>
                            <button
                                onClick={() => setOpen(false)}
                                className="text-[#D4BC7C] p-1"
                                aria-label="Fermer"
                                data-testid="chat-close"
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="bc-chat-messages" ref={scrollRef} data-testid="chat-messages">
                        {messages.map((m, i) => {
                            const isEmptyAssistant = m.role === "assistant" && !m.content && loading && i === messages.length - 1;
                            return (
                                <div
                                    key={i}
                                    className={`bc-chat-bubble ${m.role === "user" ? "bc-chat-bubble--user" : "bc-chat-bubble--bot"}`}
                                    data-testid={`chat-msg-${m.role}-${i}`}
                                >
                                    {isEmptyAssistant ? (
                                        <span className="inline-flex items-center gap-2">
                                            <Loader2 size={14} className="animate-spin" />
                                            <span style={{ opacity: 0.7 }}>Réflexion…</span>
                                        </span>
                                    ) : m.role === "assistant" ? (
                                        <>
                                            <ReactMarkdown
                                                components={{
                                                    a: ({ node, ...props }) => (
                                                        <a {...props} target="_blank" rel="noopener noreferrer" className="bc-chat-link" />
                                                    ),
                                                    p: ({ node, ...props }) => <p {...props} style={{ margin: "0 0 0.5em 0" }} />,
                                                    ul: ({ node, ...props }) => <ul {...props} style={{ margin: "0.25em 0", paddingLeft: "1.1em" }} />,
                                                    ol: ({ node, ...props }) => <ol {...props} style={{ margin: "0.25em 0", paddingLeft: "1.1em" }} />,
                                                    li: ({ node, ...props }) => <li {...props} style={{ margin: "0.15em 0" }} />,
                                                    strong: ({ node, ...props }) => <strong {...props} style={{ color: "#D4BC7C" }} />,
                                                }}
                                            >
                                                {m.content}
                                            </ReactMarkdown>
                                            {loading && i === messages.length - 1 && (
                                                <span className="bc-chat-cursor" aria-hidden="true">▊</span>
                                            )}
                                        </>
                                    ) : (
                                        m.content.split("\n").map((line, idx) => (
                                            <p key={idx} style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                                                {line}
                                            </p>
                                        ))
                                    )}
                                </div>
                            );
                        })}

                        {error && (
                            <div
                                className="text-xs px-3 py-2"
                                style={{ background: "rgba(220, 38, 38, 0.12)", color: "#fca5a5", border: "1px solid rgba(220, 38, 38, 0.35)" }}
                                data-testid="chat-error"
                            >
                                {error}
                            </div>
                        )}

                        {messages.length <= 1 && !loading && (
                            <div className="flex flex-col gap-2 mt-3">
                                <p className="text-[10px] tracking-widest uppercase" style={{ color: "#A0A0A0" }}>
                                    Suggestions
                                </p>
                                {SUGGESTIONS.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => send(s)}
                                        className="bc-chat-suggestion"
                                        data-testid={`chat-suggestion-${s.slice(0, 12)}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <form
                        className="bc-chat-input-row"
                        onSubmit={(e) => {
                            e.preventDefault();
                            send();
                        }}
                    >
                        <input
                            ref={inputRef}
                            className="bc-chat-input"
                            placeholder="Votre question…"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            disabled={loading}
                            data-testid="chat-input"
                            maxLength={2000}
                        />
                        <button
                            type="submit"
                            className="bc-chat-send"
                            disabled={loading || !input.trim()}
                            aria-label="Envoyer"
                            data-testid="chat-send"
                        >
                            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        </button>
                    </form>

                    <p
                        className="text-[9px] px-4 pb-3 text-center"
                        style={{ color: "#7a7a7a", letterSpacing: "0.12em" }}
                    >
                        IA générative — vérifiez les infos critiques auprès de l&apos;équipe Costa.
                    </p>
                </div>
            )}
        </>
    );
}
